import React, { useState } from 'react';
import { PhoneIcon, CheckIcon, ThumbsUpIcon, MessageCircleIcon, Share2Icon, PaperclipIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { hasContent } from '../RichTextEditor';
import { resolveHtml, resolveText } from '../../utils/variables';
import type { NoticeVariable } from '../../types/variables';
import type { ChannelsState } from '../../types/composer';
export type PreviewTab = 'push' | 'sms' | 'email' | 'voice' | 'website' | 'social' | 'screens';
interface NoticePreviewProps {
  channels: ChannelsState;
  title: string;
  description: string;
  categoryLabel: string;
  previewTab: PreviewTab;
  onPreviewTabChange: (tab: PreviewTab) => void;
  /** Variables used to resolve chips into real values. */
  variables: NoticeVariable[];
  /** Language the notice content is written in. */
  contentLang: 'fr' | 'en';
}
export function NoticePreview({
  channels,
  title,
  description,
  categoryLabel,
  previewTab,
  onPreviewTabChange,
  variables,
  contentLang
}: NoticePreviewProps) {
  const {
    t
  } = useLanguage();
  const [voiceTestPhone, setVoiceTestPhone] = useState('');
  const [voiceTestSent, setVoiceTestSent] = useState(false);

  /** Plain text with every variable replaced by its value. */
  const text = (html: string) => resolveText(html, variables, contentLang);
  /** Rich HTML with every variable replaced by its value. */
  const rich = (html: string) => resolveHtml(html, variables, contentLang);
  const tabs: {
    id: PreviewTab;
    label: string;
  }[] = [{
    id: 'push',
    label: t('Push', 'Push')
  }, {
    id: 'sms',
    label: t('SMS', 'SMS')
  }, {
    id: 'email',
    label: t('Courriel', 'Email')
  }, {
    id: 'voice',
    label: t('Voix', 'Voice')
  }, {
    id: 'website',
    label: t('Site web', 'Website')
  }, {
    id: 'social',
    label: t('Social', 'Social')
  }, {
    id: 'screens',
    label: t('Écrans', 'Screens')
  }];
  const isPhone = previewTab === 'push' || previewTab === 'sms' || previewTab === 'voice';
  return <section className="space-y-4" data-id="element-3931" data-mp="388h9ri7hf05k">
      <div className="flex items-center justify-between gap-2 flex-wrap" data-id="element-3932" data-mp="38bmvqoktdlx2">
        <h2 className="text-base font-semibold text-slate-900" data-id="element-3933" data-mp="3d72799vcn5qb">
          {t('Aperçu', 'Preview')}
        </h2>
        <div className="flex flex-wrap gap-1 bg-slate-200/50 p-1 rounded-lg" data-id="element-3934" data-mp="3hb05zjgrieiu">
          {tabs.map(tab => <button key={tab.id} onClick={() => {
          onPreviewTabChange(tab.id);
          setVoiceTestSent(false);
        }} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${previewTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3935" data-mp="3m77d3fkw9w2d">
              {tab.label}
            </button>)}
        </div>
      </div>

      <div className="flex justify-center" data-id="element-3936" data-mp="2atmsh1pw5nzq">
        {/* Phone mockup — push / sms / voice */}
        {isPhone && <div className="w-[300px] h-[600px] bg-white rounded-[40px] border-[8px] border-slate-900 overflow-hidden relative" data-id="element-3937" data-mp="239knn3nvmm7u">
            <div className="absolute top-0 inset-x-0 h-6 flex justify-center z-20" data-id="element-3938" data-mp="1ycc0sprs9u09">
              <div className="w-32 h-6 bg-slate-900 rounded-b-xl" data-id="element-3939" data-mp="1tav2n20oolnd" />
            </div>

            <div className="w-full h-full bg-slate-50 pt-12 px-4 relative" data-id="element-3940" data-mp="1p9t3smaoiblx">
              {previewTab === 'push' && <div className="bg-white/80 backdrop-blur-md rounded-2xl p-4 border border-slate-100 mt-4" data-id="element-3941" data-mp="1kdcgfubb019w">
                  <div className="flex items-center gap-2 mb-2" data-id="element-3942" data-mp="1fjq7ac4cmfiu">
                    <img src="https://cdn.magicpatterns.com/uploads/ckK2136PdZbnMQPqZiNST7/image.png" alt={t('Logo Ville de Brossard', 'City of Brossard logo')} className="w-5 h-5 rounded object-cover flex-shrink-0" data-id="element-3943" data-mp="1amzv3zvo042p" />
                    <span className="text-xs font-medium text-slate-500 truncate" data-id="element-3944" data-mp="wni1n77fc2n0">
                      {t('Ville de Brossard', 'City of Brossard')}
                    </span>
                    <span className="text-xs text-slate-400 ml-auto" data-id="element-3945" data-mp="30qjpk1e6ey8k">
                      {t('Maintenant', 'Now')}
                    </span>
                  </div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-1" data-id="element-3946" data-mp="2mwslh8j7s6k8">
                    {text(channels.push.title) || title || t('Titre de la notification', 'Notification title')}
                  </h4>
                  <p className="text-sm text-slate-600 leading-snug" data-id="element-3947" data-mp="2i0bmtkee29sw">
                    {text(channels.push.message) || description || t('Le message de votre notification apparaîtra ici…', 'Your notification message will appear here…')}
                  </p>
                </div>}

              {previewTab === 'sms' && <div className="mt-8 space-y-4" data-id="element-3948" data-mp="hb6224zaju7b">
                  <div className="flex justify-center" data-id="element-3949" data-mp="hbxl6gbpbzgp">
                    <span className="text-[10px] font-medium text-slate-400" data-id="element-3950" data-mp="ccurtt533hok">
                      {t("Aujourd'hui 10:42", 'Today 10:42')}
                    </span>
                  </div>
                  <div className="bg-blue-500 text-white rounded-2xl rounded-tr-sm p-3 max-w-[85%] ml-auto" data-id="element-3951" data-mp="ld9g91dchcwa">
                    <p className="text-sm leading-snug" data-id="element-3952" data-mp="101dzc6gb7qfl">
                      {text(channels.sms.text) || t('Votre message texte apparaîtra ici. Il doit être court et direct.', 'Your text message will appear here. Keep it short and direct.')}
                    </p>
                  </div>
                </div>}

              {previewTab === 'voice' && <div className="absolute inset-0 bg-[#0F172A] pt-14 px-5 flex flex-col" data-id="element-3953" data-mp="36sd5e6ktzo43">
                  <div className="flex flex-col items-center text-center" data-id="element-3954" data-mp="36upvx84nn6sr">
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-widest mb-4" data-id="element-3955" data-mp="19taddeldtddd">
                      {t('Appel entrant', 'Incoming call')}
                    </span>
                    <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center mb-3" data-id="element-3956" data-mp="1jobze1kd1zbq">
                      <PhoneIcon className="w-8 h-8 text-[#4F46E5]" data-id="element-3957" data-mp="1oqb2rbg3ow8o" />
                    </div>
                    <h4 className="text-base font-semibold text-white" data-id="element-3958" data-mp="1sq1yltkhi2k2">
                      {t('Ville de Brossard', 'City of Brossard')}
                    </h4>
                    <p className="text-xs text-slate-400 mb-4" data-id="element-3959" data-mp="22g4qtrjs7chc">
                      {t('Message vocal automatisé', 'Automated voice message')}
                    </p>
                  </div>

                  <div className="bg-white/10 rounded-2xl p-3 text-left mb-4 overflow-y-auto max-h-32" data-id="element-3960" data-mp="2cf41ffo3xi6z">
                    <p className="text-xs text-slate-200 leading-relaxed" data-id="element-3961" data-mp="2gfe9cxvgmioh">
                      {text(channels.voice.script) || t('Le script de votre appel vocal sera lu ici par une voix automatisée…', 'Your call script will be read here by an automated voice…')}
                    </p>
                  </div>

                  <div className="mt-auto bg-white rounded-2xl p-4 mb-4" data-id="element-3962" data-mp="3mzqq8jfws2r1">
                    {voiceTestSent ? <div className="text-center" data-id="element-3963" data-mp="oga3f2h4h54q">
                        <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-2" data-id="element-3964" data-mp="jh6v995uiisj">
                          <CheckIcon className="w-5 h-5 text-emerald-600" data-id="element-3965" data-mp="epynehk5z5hh" />
                        </div>
                        <p className="text-sm font-semibold text-slate-900" data-id="element-3966" data-mp="9r43tdedhcwb">
                          {t('Test envoyé !', 'Test sent!')}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-3967" data-mp="12guhjlc3ecrw">
                          {t('Un appel test est en route vers ', 'A test call is on its way to ')}
                          {voiceTestPhone}.
                        </p>
                        <button onClick={() => setVoiceTestSent(false)} className="mt-2 text-xs font-semibold text-[#4F46E5] hover:text-[#4338CA]" data-id="element-3968" data-mp="1t4jcfq9tbu0">
                          {t('Envoyer un autre test', 'Send another test')}
                        </button>
                      </div> : <>
                        <label className="block text-xs font-semibold text-slate-700 mb-1.5" data-id="element-3969" data-mp="ki1d8ihra2bp">
                          {t("Tester l'appel vocal", 'Test the voice call')}
                        </label>
                        <input type="tel" value={voiceTestPhone} onChange={e => setVoiceTestPhone(e.target.value)} placeholder="(514) 555-0199" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] mb-2" data-id="element-3970" data-mp="3588rg10f4igj" />
                        <button onClick={() => {
                  if (voiceTestPhone.trim()) setVoiceTestSent(true);
                }} disabled={!voiceTestPhone.trim()} className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-3971" data-mp="36qgzbth22rhr">
                          <PhoneIcon className="w-4 h-4" data-id="element-3972" data-mp="209lpgtf6fhgl" />{' '}
                          {t('Envoyer un test', 'Send a test')}
                        </button>
                      </>}
                  </div>
                </div>}
            </div>
          </div>}

        {/* Social post mockup */}
        {previewTab === 'social' && <div className="w-full max-w-[360px] bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-3973" data-mp="coinc0dqtshx">
            <div className="flex items-center gap-3 p-3 border-b border-slate-100" data-id="element-3974" data-mp="7pxkrub0qwqb">
              <img src="https://cdn.magicpatterns.com/uploads/kfqfiE7nmccz961Hy3x3v1/image.png" alt={t('Logo Ville de Brossard', 'City of Brossard logo')} className="w-9 h-9 rounded-full object-cover flex-shrink-0" data-id="element-3975" data-mp="3nuah9035y6m" />
              <div className="min-w-0" data-id="element-3976" data-mp="3q1asii8yyips">
                <p className="text-sm font-semibold text-slate-900 truncate" data-id="element-3977" data-mp="3l53ncroedb8w">
                  {t('Ville de Brossard', 'City of Brossard')}
                </p>
                <p className="text-[11px] text-slate-400" data-id="element-3978" data-mp="3g726mdeg1o0l">
                  {t("À l'instant · ", 'Just now · ')}
                  <span aria-hidden="true" data-id="element-3979" data-mp="1rllh7d5u66sf">🌐</span>
                </p>
              </div>
            </div>

            <div className="px-3 py-2" data-id="element-3980" data-mp="1vmx3p9teybwe">
              {hasContent(channels.social.postText) ? <div className="text-sm text-slate-700 leading-snug [&_a]:text-[#4F46E5] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold [&_img]:w-full [&_img]:rounded-lg [&_img]:my-2" dangerouslySetInnerHTML={{
            __html: rich(channels.social.postText)
          }} data-id="element-3981" data-mp="20mjgxj98rs57" /> : <p className="text-sm text-slate-400" data-id="element-3982" data-mp="1cumay8sh3uc0">
                  {t('Le texte de votre publication apparaîtra ici.', 'Your post text will appear here.')}
                </p>}
            </div>

            <div className="flex items-center justify-around px-3 py-2 border-t border-slate-100 text-slate-500" data-id="element-3983" data-mp="p6l91ipsvc5k">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-3984" data-mp="u44p8o625z05">
                <ThumbsUpIcon className="w-4 h-4" data-id="element-3985" data-mp="m3oxe8u9bqd4" /> {t("J'aime", 'Like')}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-3986" data-mp="i3erew6gszy9">
                <MessageCircleIcon className="w-4 h-4" data-id="element-3987" data-mp="vu02hypog5ll" /> {t('Commenter', 'Comment')}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-3988" data-mp="qxjgpb5ya3bo">
                <Share2Icon className="w-4 h-4" data-id="element-3989" data-mp="37p14lu8y0nq" /> {t('Partager', 'Share')}
              </span>
            </div>
          </div>}

        {/* Public screen mockup — 16:9 landscape signage */}
        {previewTab === 'screens' && <div className="w-full max-w-[400px]" data-id="element-3990" data-mp="d2qu5srn7mvl">
            <div className="rounded-xl bg-slate-900 p-2.5 shadow-sm" data-id="element-3991" data-mp="85h205cvcx42">
              <div className="aspect-video rounded-lg bg-[#0F172A] overflow-hidden flex flex-col" data-id="element-3992" data-mp="1ojqi6c6yjfjt">
                <div className="flex items-center justify-between px-4 pt-3.5 flex-shrink-0" data-id="element-3993" data-mp="1jl5s35lf6c93">
                  <img src="https://cdn.magicpatterns.com/uploads/kfqfiE7nmccz961Hy3x3v1/image.png" alt={t('Logo Ville de Brossard', 'City of Brossard logo')} className="h-6 w-6 rounded object-cover bg-white" data-id="element-3994" data-mp="31iioj3we7uta" />
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-3995" data-mp="2sk6v6u456ix5">
                    {categoryLabel}
                  </span>
                </div>

                <div className="flex-1 flex flex-col justify-center px-5 py-3 min-h-0" data-id="element-3996" data-mp="3l8ma7gn9x18h">
                  {hasContent(channels.screens.headline) ? <p className="text-lg font-bold text-white leading-tight line-clamp-2" data-id="element-3997" data-mp="3bhhtochy98bj">
                      {text(channels.screens.headline)}
                    </p> : <p className="text-lg font-bold text-slate-600 leading-tight" data-id="element-3998" data-mp="7zpw8il1nw5y">
                      {title || t('Avis important', 'Important notice')}
                    </p>}
                  {hasContent(channels.screens.bodyText) ? <p className="mt-2 text-[11px] text-slate-300 leading-snug line-clamp-4" data-id="element-3999" data-mp="2k7ww6ggs5wgo">
                      {text(channels.screens.bodyText)}
                    </p> : <p className="mt-2 text-[11px] text-slate-500 leading-snug" data-id="element-4000" data-mp="2bbfqhsr6qqsb">
                      {t('Votre message apparaîtra ici, en gros caractères.', 'Your message will appear here, in large type.')}
                    </p>}
                </div>

                <div className="px-4 pb-3 flex items-center justify-between flex-shrink-0" data-id="element-4001" data-mp="2u7fc8ltkky3g">
                  <span className="text-[9px] text-slate-500" data-id="element-4002" data-mp="2pa5m13ycwiek">
                    {t('brossard.ca', 'brossard.ca')}
                  </span>
                  {/* Slide progress bar */}
                  <span className="h-0.5 w-20 rounded-full bg-slate-700 overflow-hidden" data-id="element-4003" data-mp="3hxi00pqgbhw9">
                    <span className="block h-full w-2/3 bg-[#4F46E5]" data-id="element-4004" data-mp="3p9fz7l96229a" />
                  </span>
                </div>
              </div>
            </div>

            <p className="mt-2.5 text-xs text-slate-500 text-center" data-id="element-4005" data-mp="3u8j1ixxrhudk">
              {t(`Rotation de ${channels.screens.duration} s sur ${channels.screens.locations.length} écran${channels.screens.locations.length > 1 ? 's' : ''}`, `${channels.screens.duration} s rotation on ${channels.screens.locations.length} screen${channels.screens.locations.length > 1 ? 's' : ''}`)}
            </p>
          </div>}

        {/* Desktop mockup — email / website */}
        {(previewTab === 'email' || previewTab === 'website') && <div className="w-full max-w-[360px]" data-id="element-4006" data-mp="qq91lpnlp7rm">
            <div className="bg-white rounded-xl border border-slate-300 overflow-hidden shadow-sm" data-id="element-4007" data-mp="ut5q1wztny3h">
              <div className="h-9 bg-slate-100 border-b border-slate-200 flex items-center gap-1.5 px-3" data-id="element-4008" data-mp="zs8k6me9kjwo">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400" data-id="element-4009" data-mp="2vv3qosv6rj7v" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" data-id="element-4010" data-mp="2vt0shljvqjs2" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" data-id="element-4011" data-mp="2rvcwef6gakyl" />
                <div className="ml-2 flex-1 h-5 bg-white rounded border border-slate-200 text-[9px] text-slate-400 flex items-center px-2 truncate" data-id="element-4012" data-mp="35m86fusor83r">
                  {previewTab === 'email' ? 'mail.brossard.ca' : 'brossard.ca'}
                </div>
              </div>

              <div className="bg-white h-[440px] overflow-y-auto relative" data-id="element-4013" data-mp="3akjfi3b5pekx">
                {previewTab === 'email' && <div className="bg-white" data-id="element-4014" data-mp="3okan9mb2xyqh">
                    <div className="px-5 py-3 border-b border-slate-100" data-id="element-4015" data-mp="3ohy8fi0mc4hn">
                      <p className="text-xs text-slate-500 mb-1" data-id="element-4016" data-mp="3jlhbpzfcshsy">
                        {t('De: Ville de Brossard', 'From: City of Brossard')}
                      </p>
                      <h4 className="text-sm font-semibold text-slate-900" data-id="element-4017" data-mp="1p7q6dc424k3b">
                        {text(channels.email.subject) || title || t('Objet du courriel', 'Email subject')}
                      </h4>
                    </div>
                    <div className="p-6" data-id="element-4018" data-mp="3s2hxeypo5ldk">
                      <img src="https://cdn.magicpatterns.com/uploads/nedc6b9KaGTagFGJXbwvij/pasted-image.png" alt={t('Logo Ville de Brossard', 'City of Brossard logo')} className="h-8 w-auto object-contain mb-5" data-id="element-4019" data-mp="3s1gk0nl6kmso" />

                      <h1 className="text-xl font-bold text-slate-900 mb-3" data-id="element-4020" data-mp="3n46pitixpkxz">
                        {title || t("Titre de l'avis", 'Notice title')}
                      </h1>

                      {hasContent(channels.email.body) ? <div className="text-sm text-slate-600 leading-relaxed [&_a]:text-[#4F46E5] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold [&_img]:w-full [&_img]:rounded-lg [&_img]:my-3" dangerouslySetInnerHTML={{
                  __html: rich(channels.email.body)
                }} data-id="element-4021" data-mp="17c0q3jqo0hyt" /> : <p className="text-sm text-slate-400 leading-relaxed" data-id="element-4022" data-mp="3lj21jgt5l46u">
                          {description || t("Le contenu détaillé de votre courriel s'affichera ici.", 'The detailed content of your email will appear here.')}
                        </p>}

                      {channels.email.attachments.length > 0 && <div className="mt-5 pt-4 border-t border-slate-100" data-id="element-4023" data-mp="2xlmnqbq6bq3t">
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2" data-id="element-4024" data-mp="2xnpnvol1izm9">
                            {channels.email.attachments.length}{' '}
                            {t('pièce(s) jointe(s)', 'attachment(s)')}
                          </p>
                          <ul className="space-y-1.5" data-id="element-4025" data-mp="14aqxi0aetht4">
                            {channels.email.attachments.map(file => <li key={file.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-2.5 py-1.5" data-id="element-4026" data-mp="18ahmpzkafpvj">
                                <PaperclipIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-4027" data-mp="lcwldddziq6i" />
                                <span className="text-xs text-slate-600 truncate" data-id="element-4028" data-mp="pd81ccvek2bn">
                                  {file.name}
                                </span>
                              </li>)}
                          </ul>
                        </div>}
                    </div>
                  </div>}

                {previewTab === 'website' && <div className="relative" data-id="element-4029" data-mp="2aprl9ipa6tom">
                    <img src="https://cdn.magicpatterns.com/uploads/paDPkCcwNHTw4rcW21cM4q/image.png" alt={t('Aperçu du site web de Brossard', 'Preview of the Brossard website')} className="w-full block" data-id="element-4030" data-mp="3mm608t1xlr9g" />
                    <div className="absolute top-[13%] inset-x-0 bg-[#E7F1FB] px-3 py-3 border-y border-[#cfe0f5]" data-id="element-4031" data-mp="54v1oqamu412">
                      <div className="flex items-start justify-between gap-2" data-id="element-4032" data-mp="33pmumdqc6lhn">
                        <div className="min-w-0" data-id="element-4033" data-mp="38mn30vans0bx">
                          <span className="inline-block px-3 py-0.5 rounded-full bg-[#1D4ED8] text-white text-[10px] font-bold mb-1.5" data-id="element-4034" data-mp="3dkyaizdhdyqz">
                            {categoryLabel}
                          </span>
                          <p className="text-[11px] font-bold text-slate-900 leading-snug" data-id="element-4035" data-mp="sw0l1ffs7eg4">
                            {text(channels.website.bannerText) || title || t('Le texte de votre bannière apparaîtra ici', 'Your banner text will appear here')}
                          </p>
                          <p className="text-[10px] font-semibold text-slate-800 underline mt-1" data-id="element-4036" data-mp="28kq8xnr2iomb">
                            {text(channels.website.ctaLabel) || t("Consulter l'avis en cours", 'View the current notice')}
                          </p>
                        </div>
                        <span className="text-slate-500 text-sm leading-none" data-id="element-4037" data-mp="1trn4mi2hm7dj">✕</span>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>

            <div className="mx-auto h-2.5 w-[88%] bg-slate-300 rounded-b-xl" data-id="element-4038" data-mp="3k1q02g1oixer" />
            <div className="mx-auto h-1 w-[40%] bg-slate-200 rounded-b-lg" data-id="element-4039" data-mp="3jznce2o01gd7" />
          </div>}
      </div>
    </section>;
}